import React, { useEffect, useState, useCallback, useRef } from "react";
import ReactDOM from "react-dom";

interface TooltipProps {
  id: string;
  place?: "top" | "bottom" | "left" | "right" | "top-start";
  delayHide?: number;
  watch: any;
}

const Tooltip: React.FC<TooltipProps> = ({
  id,
  place = "top",
  delayHide = 0,
  watch,
}) => {
  const [visible, setVisible] = useState(false);
  const [content, setContent] = useState("");
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const hideTimeoutRef = useRef<NodeJS.Timeout>();


  const show = useCallback((target: HTMLElement) => {
    const tooltipContent = target.getAttribute("data-tooltip-content");
    if (!tooltipContent) return;

    const rect = target.getBoundingClientRect();
    setContent(tooltipContent);

    let x = rect.left + rect.width / 2;
    let y = rect.top;

    if (place === "top-start") {
      x = rect.left;
      y = rect.top - 8;
    } else if (place === "top") {
      x = rect.left + rect.width / 2;
      y = rect.top - 8;
    }

    setCoords({ x, y });

    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    requestAnimationFrame(() => setVisible(true));
  }, [place]);

  const hide = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setVisible(false), delayHide);
  }, [delayHide]);

  const handlersRef = useRef<Map<HTMLElement, { onEnter: (e: Event) => void; onLeave: (e: Event) => void }>>(new Map());

  const createHandlers = useCallback(() => ({
    onEnter: (e: Event) => show(e.currentTarget as HTMLElement),
    onLeave: () => hide(),
  }), [show, hide]);


  const bindEvents = useCallback(() => {
    const elements = document.querySelectorAll<HTMLElement>(
      `[data-tooltip-id="${id}"]`
    );

    handlersRef.current.forEach((handlers, el) => {
      if (!document.contains(el)) {
        el.removeEventListener("mouseenter", handlers.onEnter);
        el.removeEventListener("mouseleave", handlers.onLeave);
        handlersRef.current.delete(el);
      }
    });

    elements.forEach((el) => {
      if (handlersRef.current.has(el)) return;

      const handlers = createHandlers();
      handlersRef.current.set(el, handlers);

      el.addEventListener("mouseenter", handlers.onEnter);
      el.addEventListener("mouseleave", handlers.onLeave);
    });
  }, [id, createHandlers]);

  useEffect(() => {
    bindEvents();

    const observer = new MutationObserver(() => {
      bindEvents();
    });

    const observerTarget = document.body || document.documentElement;
    observer.observe(observerTarget, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      handlersRef.current.forEach((handlers, el) => {
        el.removeEventListener("mouseenter", handlers.onEnter);
        el.removeEventListener("mouseleave", handlers.onLeave);
      });
      handlersRef.current.clear();
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [id, place, delayHide, watch, bindEvents]);


  useEffect(() => {
    bindEvents();
  }, [watch, bindEvents]);

  const getPortalContainer = useCallback(() => {
    let container = document.getElementById("qs-tooltip-portal");
    if (!container) {
      container = document.createElement("div");
      container.id = "qs-tooltip-portal";
      document.body.appendChild(container);
    }
    return container;
  }, []);

  useEffect(() => {
    if (!visible) return;
    try {
      getPortalContainer();
    } catch (err) {
      console.warn("Tooltip: Failed to create portal container", err);
    }
  }, [visible, getPortalContainer]);

  const portalTarget = typeof document !== "undefined" 
    ? document.body || document.documentElement 
    : null;

  if (!visible || !portalTarget) return null;

  return ReactDOM.createPortal(
    <div
      className={`qs_tooltip tooltip-${place}`}
      style={{
        position: "fixed",
        left: coords.x,
        top: coords.y,
        transform:
          place === "top-start"
            ? "translateY(-100%)"
            : "translate(-50%, -100%)",
        pointerEvents: "none",
      }}
    >
      {content}
      <div className="qs_tooltip_arrow" />
    </div>,
    portalTarget
  );
};

export default Tooltip;
